import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { BrandButton } from "@/components/PageHeader";
import { Field, Pick } from "@/components/event-setup/BasicsSection";
import { LookupInput } from "@/components/special-receipts/MemberLookup";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";

export const METHODS = ["Cash", "MTN MoMo", "Telecel Cash", "AT Money", "Card", "Bank Transfer", "Cheque"];
const MANUAL = "__manual";
const NO_EVENT = "__none";
const EMPTY = { payer_name: "", phone: "", email: "", pin: "", type: MANUAL, description: "", event_id: NO_EVENT, amount: "", method: "Cash", status: "Paid", note: "",
  cheque_no: "", cheque_bank: "", cheque_issuer: "", reason: "" };

const fromReceipt = (r) => ({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).filter((k) => r[k] != null).map((k) => [k, r[k]])),
  type: r.type_id || MANUAL, event_id: r.event_id || NO_EVENT, amount: String(r.amount), reason: "" });

const Description = ({ f, set, types }) => {
  const pickType = (v) => {
    const t = types.find((x) => x.id === v);
    set({ type: v, description: t ? t.name : "", amount: t?.default_amount ? String(t.default_amount) : (f.autoAmount ? "" : f.amount), autoAmount: !!t?.default_amount });
  };
  return (
    <>
      <Field label="Payment type">
        <Pick value={f.type} onChange={pickType} testId="special-type-select"
          options={[...types.map((t) => ({ value: t.id, label: t.name })), { value: MANUAL, label: "Enter description manually" }]} />
      </Field>
      <Field label="Description (shown on receipt)">
        <Input value={f.description} onChange={(e) => set({ description: e.target.value })} placeholder={f.type === MANUAL ? "e.g. Branch levy, Workshop materials" : ""} data-testid="special-description-input" />
      </Field>
    </>
  );
};

const Cheque = ({ f, txt }) => (
  <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-4 rounded-md border border-dashed border-[#E60000]/40 p-3" data-testid="special-cheque-fields">
    <Field label="Cheque number"><Input value={f.cheque_no} onChange={txt("cheque_no")} className="font-mono" data-testid="special-cheque-no-input" /></Field>
    <Field label="Bank"><Input value={f.cheque_bank} onChange={txt("cheque_bank")} data-testid="special-cheque-bank-input" /></Field>
    <Field label="Issuer"><Input value={f.cheque_issuer} onChange={txt("cheque_issuer")} placeholder="Name on the cheque" data-testid="special-cheque-issuer-input" /></Field>
  </div>
);

const Payer = ({ f, set }) => {
  const pick = (m) => set({ payer_name: m.name || f.payer_name, phone: m.phone || "", email: m.email || "", pin: m.pin || "" });
  const typePin = async (v) => {
    set({ pin: v });
    if (v.trim().length < 5) return;
    const r = await api.get("/special-receipts/member-lookup", { params: { q: v.trim() } }).catch(() => null);
    const hit = r?.data.find((m) => (m.pin || "").toUpperCase() === v.trim().toUpperCase());
    if (hit) { pick(hit); toast.success(`Filled in details for ${hit.name}`); }
  };
  return (
    <>
      <Field label="Received from (payer name)" className="sm:col-span-2"><LookupInput value={f.payer_name} onChange={(v) => set({ payer_name: v })} onPick={pick} placeholder="Type a name to search member accounts" testId="special-payer-input" /></Field>
      <Field label="Phone"><LookupInput value={f.phone} onChange={(v) => set({ phone: v })} onPick={pick} placeholder="+233 …" testId="special-phone-input" /></Field>
      <Field label="Email"><Input value={f.email} onChange={(e) => set({ email: e.target.value })} data-testid="special-email-input" /></Field>
      <Field label="AHPC PIN (optional)"><LookupInput value={f.pin} onChange={typePin} onPick={pick} placeholder="Type the PIN to fill in the rest" className="font-mono" testId="special-pin-input" /></Field>
    </>
  );
};

export const NewReceiptDialog = ({ open, onClose, onSaved, receipt, defaultEventId }) => {
  const { user, events } = useApp();
  const [f, setF] = useState(EMPTY);
  const [types, setTypes] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (p) => setF((s) => ({ ...s, ...p }));
  const txt = (k) => (e) => set({ [k]: e.target.value });
  useEffect(() => {
    if (open) {
      setF(receipt ? fromReceipt(receipt) : { ...EMPTY, event_id: defaultEventId || NO_EVENT });
      api.get("/special-receipts/types").then((r) => setTypes(r.data));
    }
  }, [open, receipt, defaultEventId]);
  const save = async () => {
    if (f.payer_name.trim().length < 2) return toast.error("Enter the payer's name");
    if (f.description.trim().length < 2) return toast.error("Choose a payment type or enter a description");
    if (!(Number(f.amount) > 0)) return toast.error("Enter an amount greater than zero");
    if (f.method === "Cheque" && !(f.cheque_no.trim() && f.cheque_bank.trim() && f.cheque_issuer.trim())) return toast.error("Enter the cheque number, bank and issuer");
    if (receipt && f.reason.trim().length < 3) return toast.error("Give a reason for this edit");
    setBusy(true);
    try {
      const { type, event_id, autoAmount, reason, ...rest } = f;
      const body = { ...rest, amount: Number(f.amount), type_id: type === MANUAL ? null : type, event_id: event_id === NO_EVENT ? null : event_id, recorded_by: receipt?.recorded_by ?? (user?.name || "") };
      const r = receipt ? await api.put(`/special-receipts/${receipt.id}`, { ...body, reason }) : await api.post("/special-receipts", body);
      toast.success(receipt ? `Receipt ${r.data.receipt_no} updated` : `Receipt ${r.data.receipt_no} created`);
      onSaved(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="special-new-dialog">
        <DialogHeader><DialogTitle className="font-heading">{receipt ? `Edit ${receipt.receipt_no}` : "New special receipt"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Payer f={f} set={set} />
          <Field label="Related event (optional)">
            <Pick value={f.event_id} onChange={(v) => set({ event_id: v })} testId="special-event-select" options={[{ value: NO_EVENT, label: "Not linked to an event" }, ...events.map((e) => ({ value: e.id, label: e.title }))]} />
          </Field>
          <Description f={f} set={set} types={types} />
          <Field label="Amount (GH₵)"><Input type="number" min="0" step="0.01" value={f.amount} onChange={(e) => set({ amount: e.target.value, autoAmount: false })} className="font-mono" data-testid="special-amount-input" /></Field>
          <Field label="Payment method"><Pick value={f.method} onChange={(v) => set({ method: v })} testId="special-method-select" options={METHODS.map((m) => ({ value: m, label: m }))} /></Field>
          <Field label="Status"><Pick value={f.status} onChange={(v) => set({ status: v })} testId="special-status-select" options={[{ value: "Paid", label: "Paid" }, { value: "Pending", label: "Pending (awaiting confirmation)" }]} /></Field>
          {f.method === "Cheque" && <Cheque f={f} txt={txt} />}
          <Field label="Note (admin only, not shown on the receipt)" className="sm:col-span-2"><Textarea rows={2} value={f.note} onChange={txt("note")} data-testid="special-note-input" /></Field>
          {receipt && <Field label="Reason for this edit" className="sm:col-span-2"><Textarea rows={2} value={f.reason} onChange={txt("reason")} placeholder="e.g. Wrong amount entered" data-testid="special-edit-reason-input" /></Field>}
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <BrandButton variant="outline" onClick={onClose} data-testid="special-cancel-button">Cancel</BrandButton>
          <BrandButton onClick={save} disabled={busy} data-testid="special-save-button">{busy ? "Saving…" : receipt ? "Save changes" : "Create receipt"}</BrandButton>
        </div>
      </DialogContent>
    </Dialog>
  );
};
